// Dev-only dashboard fixtures + their dispatcher, split out of `mocks.ts` (arc
// 101 slice 09) to keep each file focused and under the 150-LOC rule. One
// built-in org board (Fleet) + a custom board, so the scope router shows two
// groups; each detail fixture exercises the markdown + list presenters. The
// pager mirrors prod's deferred resolver — an empty page — so widgets render
// their empty state (the board still draws). `mockFetch` delegates here.
//
// The write half (#1033) is previewable but body-blind: the shared dispatcher
// hands this module a path + a method only, so a create mints a
// generically-named board and a rename echoes the board unchanged. Enough for
// the list to visibly grow and shrink with no backend; the real bodies are
// asserted in the route tests against a stubbed `fetch`.

import { unhandledMockRoute } from './unhandled-mock-route';

const at = (day: string): string => `${day}T12:00:00.000Z`;

const fleetSummary = {
  id: 'dsh_fleet',
  name: 'Fleet',
  scope: 'org',
  owner_user_id: null,
  builtin: true,
  widget_count: 2,
  created_iso: at('2026-05-01'),
  updated_iso: at('2026-05-01'),
};
const customSummary = {
  id: 'dsh_custom',
  name: 'My board',
  scope: 'custom',
  owner_user_id: null,
  builtin: false,
  widget_count: 1,
  created_iso: at('2026-06-01'),
  updated_iso: at('2026-06-01'),
};

const boards = [fleetSummary, customSummary];

const fleetDetail = {
  ...fleetSummary,
  layout: {
    rows: [
      { widgetId: 'dwg_md', span: 12 },
      { widgetId: 'dwg_list', span: 12 },
    ],
  },
  widgets: [
    {
      id: 'dwg_md',
      spec: {
        kind: 'markdown',
        title: 'Fleet',
        content: '## The fleet at a glance\n\nLive, read-only.',
      },
      created_iso: at('2026-05-01'),
      updated_iso: at('2026-05-01'),
    },
    {
      id: 'dwg_list',
      spec: {
        kind: 'list',
        title: 'Task queue',
        query: { source: 'platform', resource: 'tasks' },
        capabilities: { sort: [], filter: [], paginate: true },
        primary_key: 'title',
        secondary_key: 'status',
        page_size: 25,
      },
      created_iso: at('2026-05-01'),
      updated_iso: at('2026-05-01'),
    },
  ],
};
const customDetail = {
  ...customSummary,
  layout: { rows: [{ widgetId: 'dwg_note', span: 12 }] },
  widgets: [
    {
      id: 'dwg_note',
      spec: { kind: 'markdown', title: 'My board', content: '## My board\n\nA custom view.' },
      created_iso: at('2026-06-01'),
      updated_iso: at('2026-06-01'),
    },
  ],
};

/** Detail fixture per dashboard id — clicking "My board" renders its own board, not Fleet. */
const details: Record<string, unknown> = { dsh_fleet: fleetDetail, dsh_custom: customDetail };

let minted = 0;

/** Mint a custom board and file it, so the preview's list grows on create. */
function mintBoard(): unknown {
  minted += 1;
  const id = `dsh_new_${minted}`;
  const summary = { ...customSummary, id, name: `New board ${minted}` };
  const widget = {
    id: `dwg_${id}`,
    spec: { kind: 'markdown', title: summary.name, content: '## New board\n\nAuthored here.' },
    created_iso: summary.created_iso,
    updated_iso: summary.updated_iso,
  };
  const detail = {
    ...summary,
    layout: { rows: [{ widgetId: widget.id, span: 12 }] },
    widgets: [widget],
  };
  boards.push(summary);
  details[id] = detail;
  return detail;
}

/**
 * Handle a `/v1/dashboards*` request, or return `null` if `path` is not a
 * dashboard route (so `mockFetch` falls through). The pager is POST-only; the
 * list is GET (read) or POST (create); one board is GET / PATCH / DELETE. Any
 * other method or an unknown id rejects, so a bad link fails fast in dev.
 */
export function dashboardMock(path: string, method: string): Promise<unknown> | null {
  if (path !== '/v1/dashboards' && !path.startsWith('/v1/dashboards/')) return null;
  const reject = (): Promise<never> => unhandledMockRoute(method, path);

  // Pager (most specific): POST .../widgets/:wid/page → deferred-resolver empty page.
  if (path.startsWith('/v1/dashboards/') && path.endsWith('/page')) {
    return method === 'POST' ? Promise.resolve({ rows: [], cursor: null }) : reject();
  }
  if (path === '/v1/dashboards') {
    // `Page` envelope (arc 101 slice 03 sweep, task #42).
    if (method === 'GET') return Promise.resolve({ data: boards, next_cursor: null });
    return method === 'POST' ? Promise.resolve(mintBoard()) : reject();
  }
  // One board, dispatched by id: read / rename (body-blind echo) / delete.
  const id = path.slice('/v1/dashboards/'.length);
  const detail = details[id];
  if (!detail) return reject();
  if (method === 'GET' || method === 'PATCH') return Promise.resolve(detail);
  if (method !== 'DELETE') return reject();
  const i = boards.findIndex((b) => b.id === id);
  if (i >= 0) boards.splice(i, 1);
  delete details[id];
  return Promise.resolve({ id, deleted: true });
}

/** Restore the pristine fixtures — the shared reset seam (`./mock-state.ts`). */
const PRISTINE_BOARDS = boards.map((row) => structuredClone(row));
const PRISTINE_DETAILS = structuredClone(details);
const PRISTINE_MINTED = minted;

export function resetDashboardMocks(): void {
  boards.splice(0, boards.length, ...PRISTINE_BOARDS.map((row) => structuredClone(row)));
  for (const key of Object.keys(details)) delete details[key];
  Object.assign(details, structuredClone(PRISTINE_DETAILS));
  minted = PRISTINE_MINTED;
}
